Creates one outbound AI-agent contact job for the authenticated organization.

Use this endpoint when an external CRM, funnel, journey builder, or backend system needs to trigger a contact attempt as part of its own automation. Each attempted phone becomes one singular `Call`, but the public creation response returns the parent `public_api_call_contact` job.

<ApiCodeExamples exampleId="callsCreate" />

<ApiResponseExamples exampleId="callsCreate" />

## Required scope

- `calls.write`
- `calls.authorize_requested` only when the body includes `call_authorization`

`calls.write` can create calls and also allows the response for this creation request to include the detail it just created. It does not replace `calls.read` for generic list or detail reads.

## Headers

- `Authorization` (`string`, required): bearer token for the public API credential.
- `Idempotency-Key` (`string`, required): stable key for this creation attempt. It can also be sent in the JSON body as `idempotency_key`.
  Rules:
  `1..255` visible ASCII characters using letters, numbers, `.`, `_`, `-`, `:`, or `/`.
- `Content-Type` (`string`, required): `application/json`.

Idempotency is required for this endpoint. Send the key either in the `Idempotency-Key` header or in the `idempotency_key` body field, never in both places at once. The key is stored per organization, credential, method, path, and key for 24 hours. Retrying the same key with the same payload returns the stored response. Retrying the same key with a different payload returns `409 Conflict`.

## Body

- `contact` (`object`, required): CRM contact to attempt.
- `contact.identity.kind` (`string`, required): `cpf`, `cnpj`, `external_id`, or `crm_id`.
- `contact.identity.value` (`string`, required): strong CRM identity value.
- `contact.name` (`string`, optional): display name used to materialize/update the contact.
- `contact.phones` (`array<object>`, required): 1 to 5 phones in attempt order.
- `contact.phones[].number` (`string`, required): Brazilian destination number.
- `contact.phones[].label` (`string`, optional): phone label.
- `contact.phones[].priority` (`integer`, optional): explicit priority; when omitted, request order is preserved.
- `ai_agent_id` (`uuid`, required): public id from `GET /public/v1/agents/`.
- `client_reference` (`string`, optional): opaque client-defined reference for this contact job, limited to 255 printable characters. Leading or trailing whitespace is rejected. Prefer an immutable internal id and never send secrets.
- `call_authorization` (`object`, optional): immutable authorization for a call explicitly requested by the user. Requires the additional `calls.authorize_requested` scope.
- `call_authorization.reason`: must be `user_requested`.
- `call_authorization.phone`: exact authorized phone, also present in `contact.phones`.
- `call_authorization.captured_at`: ISO 8601 request timestamp, at most 24 hours old, with 5 minutes of future clock tolerance.
- `call_authorization.source`: `instagram_lead_form`, `site_lead_form`, `landing_contact_tel_lead_form`, or `chatbot_tool`.
- `call_authorization.reference`: must exactly match `client_reference`.
- `idempotency_key` (`string`, optional): alternative to the `Idempotency-Key` header.
- `dynamic_variables` (`object`, conditionally required): required when the selected agent exposes variables.
  Values must be non-empty strings and each value is limited to 600 characters.
- `pre_call_script_id` (`uuid`, conditionally required): id of an active script with `event_type=pre_call` and a configured pre-call policy. It is required when the agent returns `requires_pre_call_script=true` from `GET /public/v1/agents/`.
- `script_ids` (`array<uuid>`, optional): active call-related scripts from `GET /public/v1/scripts/`.
- `retry_policy` (`object`, optional): durable dial retry policy. When omitted, legacy behavior is preserved: one immediate pass over the phones, with no later rescheduling.
- `retry_policy.max_attempts` (`integer`, required inside `retry_policy`): maximum total calls actually queued for the job, including the first one. Accepts `1` through `10`.
- `retry_policy.delay_min_seconds` (`integer`, required inside `retry_policy`): minimum delay before another attempt. Accepts `0` through `86400`.
- `retry_policy.delay_max_seconds` (`integer`, required inside `retry_policy`): maximum delay before another attempt. Accepts `0` through `86400` and must be greater than or equal to the minimum.
- `transfer_target` (`object`, conditionally required): required when the agent demands a transfer target and rejected when the agent does not demand one.
- `safety` (`object`, optional): configurable repeat-call guards. When omitted, all guards are enabled.

`dynamic_variables` must contain every variable listed by the selected agent. Additional keys are accepted as context for pre-call and post-call scripts even when they are not referenced by the agent prompt. Every value still must pass the non-empty string and size validations; keys beginning with `sys__` are reserved.

`client_reference` is operational metadata and is never added to `dynamic_variables` or sent to the AI agent. It is part of the idempotency fingerprint: reusing the same idempotency key with another reference returns `409 Conflict`.

`pre_call_script_id` is separate from `script_ids`: the pre-call script runs before any `Call` exists. It can authorize the call, request a retry, or deny creation. Variables approved by the pre-call script are merged into the call variables. On denial or while waiting for retry, `current_call` remains `null` and no call is created.

When `retry_policy` is provided, each retry delay is randomly selected from the inclusive `delay_min_seconds` to `delay_max_seconds` interval. The platform dials untried phones first, then round-robins only through phones whose previous result allows another attempt. `no_answer` and equivalent transient outcomes may be retried. Frozen authorization covers the same retry cycle, but a new opt-out after `captured_at` terminates that cycle.

## Safety guards

These guards are enabled by default:

- `safety.skip_if_called_today` (`boolean`, default `true`): skips phones that already had a call created today.
- `safety.skip_if_reached` (`boolean`, default `true`): blocks the contact when it was already reached before.
- `safety.skip_if_converted` (`boolean`, default `true`): blocks the contact when it already converted before.

The client may send `false` for one of these flags when it intentionally wants to retry. Blocked and invalid phones are never dialed. An opt-out can only be superseded by newer `call_authorization` for the exact phone and same `client_reference`, submitted by a credential with `calls.authorize_requested`; history is not deleted. Without it, the normal opt-out block remains.

When a guard blocks creation, the API returns `409 Conflict` with `error=call_safety_blocked` and `reason` as one of:

- `already_called_today`
- `previously_reached`
- `previously_converted`
- `previously_optout`
- `blocked_phone`
- `invalid_phone`

When every submitted phone is skipped before the first attempt, the API returns `409 Conflict` with `error=no_eligible_phone` and the first ineligible phone `reason`.

## Transfer target

Use one of these shapes:

```json
{ "type": "user", "id": "44a7cfd6-8cb0-4efa-bd9c-4c7ca1f56d2a" }
```

```json
{ "type": "group", "id": "2c31d73a-d927-47b8-a019-273e3abf582c" }
```

Users must be active and have an active eligible telephony endpoint. Transfer groups must be active and have at least one eligible active member.

Accepted `transfer_target.type` values: `user`, `group`.

## Response `201`

The endpoint returns `201 Created` when the request was accepted and persisted. The first phone may be immediately `queued`, or it may be accepted into a waiting state.

- `id` (`uuid`): public id of the parent `public_api_call_contact` job.
- `resource_type` (`string`): always `public_api_call_contact`.
- `status` (`string`): parent job status.
  Common initial values:
  `in_progress`, `waiting_capacity`, `waiting_operating_hours`
- `client_reference` (`string|null`): exact opaque reference accepted from the request, or `null` when omitted.
- `call_authorization` (`object`): operational snapshot of accepted authorization, or `{}` when omitted. It is not sent through `dynamic_variables` or to the agent.
- `contact` (`object`): contact and normalized strong identity.
- `current_call` (`object|null`): current real attempt, with `id`, `to_number`, and `status`.
- `phones` (`array<object>`): job phones with `position`, `status`, `status_reason`, `call_id`, and `last_call_status`.
- `ai_agent_id` (`uuid`): selected agent id.
- `pre_call_script_id` (`uuid|null`): selected pre-call script, when present.
- `pre_call` (`object`): safe gate audit with `attempt_count`, `reason_code`, `next_retry_at`, and `execution_id`.
- `retry_policy` (`object|null`): immutable snapshot of the accepted policy, or `null` for the legacy flow.
- `attempts` (`object|null`): dial progress with `count`, `remaining`, and `next_attempt_at`, or `null` for the legacy flow.
- `script_ids` (`array<uuid>`): accepted script ids.
- `transfer_target` (`object|null`): accepted transfer target.
- `dynamic_variables` (`object`): accepted dynamic-variable values.
- `dynamic_variable_keys` (`array<string>`): accepted dynamic-variable keys.
- `idempotency_key_replayed` (`boolean`): `true` when the response came from an idempotency replay.
- `safety` (`object`): safety guards effectively applied for this creation.
- `capacity.state` (`string|null`): `waiting` when the current attempt is waiting for capacity.
- `capacity.wait_expires_at` (`datetime|null`): deadline for capacity wait.
- `operating_hours.state` (`string|null`): `waiting` when the current attempt is waiting for operating hours.
- `operating_hours.wait_expires_at` (`datetime|null`): deadline for operating-hours wait.
- `created_at` (`datetime`): call creation time.

## Waiting states

`waiting_retry` means the job is waiting for the durable deadline exposed in `attempts.next_attempt_at` before another dial. The platform reconciles overdue deadlines after worker restart or redelivery without creating two calls for the same attempt.

`waiting_capacity` means the platform accepted the call, but all relevant concurrency slots were busy. The call does not consume a slot while waiting and is promoted by an internal dispatcher when capacity opens. If no slot opens within 15 minutes, the terminal status becomes `capacity_timeout`.

`waiting_operating_hours` means the platform accepted the call outside the organization's operating window. The dispatcher resumes evaluation when the window opens. If the tolerated waiting window expires before dispatch, the terminal status becomes `operating_window_timeout`.

When pre-call returns retry, the job remains `pending`, `current_call` stays `null`, and `pre_call.next_retry_at` reports the next attempt. If all attempts are exhausted, the job ends as `failed`. When pre-call denies the call, the job ends as `cancelled`. In both cases, no `Call` is created.

The counters are independent: `pre_call.attempt_count` measures pre-call gate executions; `attempts.count` increases only when a `Call` actually enters `queued`. Waiting for capacity or operating hours does not consume a dial attempt.

External automations should persist the parent job `id` and the current attempt `current_call.id`. To track the current attempt, call `GET /public/v1/calls/{current_call.id}/`. To list every attempt for the job, call `GET /public/v1/calls/?public_api_call_contact_id={id}`.

## Expected errors

- `400 Bad Request`: invalid body, missing `Idempotency-Key`, invalid phone, missing or extra dynamic variables, invalid transfer target, non-outbound agent, missing required pre-call, or rejected script id.
- `401 Unauthorized`: invalid, expired, revoked, missing, or IP-blocked token.
- `403 Forbidden`: the credential does not have `calls.write`, or the organization cannot create AI calls.
- `404 Not Found`: referenced agent, script, user, or group is not available to the organization.
- `409 Conflict`: idempotency key is in progress, was already used with a different payload, creation was blocked by a safety guard, or no eligible phone exists.
- `429 Too Many Requests`: credential rate limit exceeded.
